<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch, type WatchStopHandle } from 'vue';
import { type LocationQuery, useRoute } from 'vue-router';
import { type Location, type SearchResults } from '@/views/search/types.ts';
import SearchPagination from '@/views/search/SearchPagination.vue';
import { useI18n } from 'vue-i18n';
import SearchResult from '@/views/search/SearchResult.vue';
import { fetchLocation } from '@/utilities/fetchLocation.ts';
import { getNearbySearchResults, getSearchResults } from '@/api';
import { getCanonicalUrl, parseNumber, parseString } from '@/utilities';
import { useHead } from '@unhead/vue';
import SearchPills from '@/views/search/SearchPills.vue';
import AdUnit from '@/components/AdUnit.vue';
import SubscribeCard from '@/views/search/SubscribeCard.vue';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import { useSearchFiltersStore } from '@/views/search/searchFiltersStore.ts';
import SectionHeader from '@/components/SectionHeader.vue';

const { t, locale } = useI18n();
const route = useRoute();
const loading = ref(true);
const filters = useSearchFiltersStore();
const priceTypeId = computed(() => parseNumber(route.query.priceTypeId));
const searchLocation = ref<Location | undefined>(undefined);
const searchResults = ref<number[]>([]);
const page = ref(1);
const pages = ref(1);
const nearbySearchResults = ref<number[]>([]);
const nearbyPage = ref(1);
const nearbyPages = ref(0);
const loadingMoreNearby = ref(false);
let nearbyRequestId = 0;

const regionGenitiveName = (location: Location) => location.genitiveName || t('views.search.results.header.region-genitive-default', { name: location.name });

const searchResultsLocation = computed(() => {
  const location = searchLocation.value;
  if (!location) return '';
  if (location.kind === 'region') return t('views.search.results.header.of-region', { name: location.name, genitiveName: regionGenitiveName(location) });
  if (location.kind === 'city') return t('views.search.results.header.of-city', { name: location.name });
  return t('views.search.results.header.of-location', { name: location.name });
});

const nearbySearchResultsTitle = computed(() => {
  const location = searchLocation.value;
  if (!location) return '';
  const categories = filters.appliedCategories.map((category) => category.text.toLocaleLowerCase(locale.value));
  const subject = categories.length > 0 ? t('views.search.results.nearby.subject-of-categories', { categories: new Intl.ListFormat(locale.value, { type: 'conjunction' }).format(categories) }) : t('views.search.results.nearby.subject');
  if (location.kind === 'region') return t('views.search.results.nearby.header-of-region', { subject, name: location.name, genitiveName: regionGenitiveName(location) });
  return t('views.search.results.nearby.header', { subject, name: location.name });
});

const fetchSearchResults = async (query: LocationQuery): Promise<SearchResults> => {
  const cityId = parseNumber(query.cityId);
  const regionId = parseNumber(query.regionId);
  const location = parseString(query.location);
  const distance = parseNumber(query.distance);
  const priceTypeId = parseNumber(query.priceTypeId);
  const priceMin = parseNumber(query.priceMin);
  const priceMax = parseNumber(query.priceMax);
  const categoryIds = parseString(query.categoryIds);
  const bedroomsMin = parseNumber(query.bedroomsMin);
  const bedroomsMax = parseNumber(query.bedroomsMax);
  const bathroomsMin = parseNumber(query.bathroomsMin);
  const bathroomsMax = parseNumber(query.bathroomsMax);
  const page = parseNumber(query.page);
  const fetch = await getSearchResults({
    query: {
      cityId: cityId?.toString(),
      regionId: regionId?.toString(),
      location,
      distance: distance,
      priceTypeId: priceTypeId?.toString(),
      priceMin: priceMin,
      priceMax: priceMax,
      categoryIds: categoryIds,
      bedroomsMin: bedroomsMin,
      bedroomsMax: bedroomsMax,
      bathroomsMin: bathroomsMin,
      bathroomsMax: bathroomsMax,
      page: page,
    },
  });
  if (fetch.response?.status == 200 && fetch.data) {
    return {
      items: fetch.data.items,
      page: fetch.data.page,
      pages: fetch.data.pages,
    };
  } else {
    throw Error(`Fetching search results failed: ${fetch.error}`);
  }
};

const fetchNearbySearchResults = async (query: LocationQuery, page: number): Promise<SearchResults> => {
  const cityId = parseNumber(query.cityId);
  const regionId = parseNumber(query.regionId);
  const location = parseString(query.location);
  const categoryIds = parseString(query.categoryIds);
  const fetch = await getNearbySearchResults({
    query: {
      cityId: cityId?.toString(),
      regionId: regionId?.toString(),
      location,
      distance: parseNumber(query.distance),
      priceTypeId: parseNumber(query.priceTypeId)?.toString(),
      priceMin: parseNumber(query.priceMin),
      priceMax: parseNumber(query.priceMax),
      categoryIds: categoryIds,
      bedroomsMin: parseNumber(query.bedroomsMin),
      bedroomsMax: parseNumber(query.bedroomsMax),
      bathroomsMin: parseNumber(query.bathroomsMin),
      bathroomsMax: parseNumber(query.bathroomsMax),
      page,
    },
  });
  if (fetch.response?.status == 200 && fetch.data) {
    return {
      items: fetch.data.items,
      page: fetch.data.page,
      pages: fetch.data.pages,
    };
  } else {
    throw Error(`Fetching nearby search results failed: ${fetch.error}`);
  }
};

const emptySearchResults: SearchResults = { items: [], page: 1, pages: 0 };

const updateSearchResults = async () => {
  loading.value = true;
  const requestId = ++nearbyRequestId;

  const results = await Promise.all([fetchLocation(route.query), fetchSearchResults(route.query)]);
  const location = results[0];
  const fetchedSearchResults = results[1];
  const isLastPage = fetchedSearchResults.page >= fetchedSearchResults.pages;
  const fetchedNearbySearchResults = location && isLastPage ? await fetchNearbySearchResults(route.query, 1).catch(() => emptySearchResults) : emptySearchResults;

  if (requestId !== nearbyRequestId) return;

  searchLocation.value = location;
  searchResults.value = fetchedSearchResults.items;
  page.value = fetchedSearchResults.page;
  pages.value = fetchedSearchResults.pages;
  nearbySearchResults.value = fetchedNearbySearchResults.items;
  nearbyPage.value = fetchedNearbySearchResults.page;
  nearbyPages.value = fetchedNearbySearchResults.pages;

  loading.value = false;
};

const loadMoreNearbySearchResults = async () => {
  if (loadingMoreNearby.value) return;

  loadingMoreNearby.value = true;
  const requestId = nearbyRequestId;
  try {
    const fetchedNearbySearchResults = await fetchNearbySearchResults(route.query, nearbyPage.value + 1);
    if (requestId !== nearbyRequestId) return;

    nearbySearchResults.value = [...nearbySearchResults.value, ...fetchedNearbySearchResults.items];
    nearbyPage.value = fetchedNearbySearchResults.page;
    nearbyPages.value = fetchedNearbySearchResults.pages;
  } finally {
    loadingMoreNearby.value = false;
  }
};

let watchHandle: WatchStopHandle | undefined;

const updateAndWatch = async () => {
  if (!watchHandle) {
    await updateSearchResults();
    watchHandle = watch(
      () => route.fullPath,
      async (newRoute, oldRoute) => {
        if (newRoute.split('?')[0] === oldRoute.split('?')[0]) {
          await updateSearchResults();
        }
      }
    );
  }
};

const stopWatching = () => {
  if (watchHandle) {
    watchHandle();
    watchHandle = undefined;
  }
};

onMounted(updateAndWatch);
onBeforeUnmount(stopWatching);

const searchResultsTitle = computed(() => `${t('views.search.results.header.results')} ${searchResultsLocation.value}`.trim());

useHead({
  title: () => t('views.search.title', { header: searchResultsTitle.value }),
  meta: () => {
    return [
      {
        name: 'description',
        content: t('views.home.meta.description'),
      },
      { property: 'og:title', content: t('views.search.title', { header: searchResultsTitle.value }) },
      { property: 'og:description', content: t('views.home.meta.description') },
      { property: 'og:url', content: getCanonicalUrl(route.path) },
      { name: 'twitter:title', content: t('views.search.title', { header: searchResultsTitle.value }) },
      { name: 'twitter:description', content: t('views.home.meta.description') },
      { name: 'twitter:card', content: 'summary' },
    ];
  },
  script: () => [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'SearchResultsPage',
        name: t('views.search.title', { header: searchResultsTitle.value }),
        url: getCanonicalUrl(route.path),
      }),
    },
  ],
});
</script>

<template>
  <section class="mb-6">
    <SectionHeader as="h1">
      {{ t('views.search.results.header.results') }}
      <template v-if="searchResultsLocation.length > 0"> {{ searchResultsLocation }}</template>

      <template v-if="searchLocation?.kind === 'location'" #actions>
        <div class="flex items-center gap-2 text-sm font-normal text-gray-700">
          <label class="whitespace-nowrap" for="distance">{{ t('views.search.results.header.distance') }}</label>
          <span class="inline-grid grid-cols-1">
            <select
              id="distance"
              v-model.number="filters.distance"
              class="focus-visible:outline-ph-green-600 col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline-2 focus-visible:-outline-offset-2 sm:text-sm/6"
              name="distance"
            >
              <option value="5">5 km</option>
              <option value="10">10 km</option>
              <option value="25">25 km</option>
              <option value="50">50 km</option>
              <option value="100">100 km</option>
            </select>
            <ChevronDownIcon aria-hidden="true" class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4" />
          </span>
        </div>
      </template>
    </SectionHeader>
    <SearchPills />
    <template v-if="!loading">
      <p v-if="searchResults.length == 0">{{ nearbySearchResults.length > 0 ? t('views.search.results.no-results-in-location') : t('views.search.results.no-results') }}</p>
      <ul v-else class="xs:grid-cols-2 grid grid-cols-1 gap-6 p-1 md:grid-cols-3 lg:grid-cols-4" role="list">
        <SubscribeCard />
        <SearchResult v-for="(projectId, index) in searchResults" :key="projectId" :index="index" :price-type-id="priceTypeId" :project-id="projectId" />
      </ul>
      <SearchPagination v-if="pages > 1" :page="page" :pages="pages" class="mt-10" />
    </template>
  </section>
  <section v-if="!loading && nearbySearchResults.length > 0" class="mb-6">
    <SectionHeader>{{ nearbySearchResultsTitle }}</SectionHeader>
    <ul class="xs:grid-cols-2 grid grid-cols-1 gap-6 p-1 md:grid-cols-3 lg:grid-cols-4" role="list">
      <SearchResult v-for="(projectId, index) in nearbySearchResults" :key="projectId" :index="index" :price-type-id="priceTypeId" :project-id="projectId" />
    </ul>
    <div v-if="nearbyPage < nearbyPages" class="mt-10 flex justify-center">
      <button
        :disabled="loadingMoreNearby"
        class="border-ph-green-600 text-ph-green-700 hover:bg-ph-green-50 focus-visible:outline-ph-green-600 cursor-pointer rounded-lg border-2 px-6 py-2.5 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-default disabled:opacity-60"
        type="button"
        @click="loadMoreNearbySearchResults"
      >
        {{ loadingMoreNearby ? t('views.search.results.nearby.loading') : t('views.search.results.nearby.load-more') }}
      </button>
    </div>
  </section>
  <AdUnit :cityId="route.query.cityId" adUnitLocation="Leaderboard" />
</template>

<style lang="css" scoped></style>
